package org.teddapp.project.server

import io.ktor.client.HttpClient
import io.ktor.client.plugins.timeout
import io.ktor.client.plugins.pluginOrNull
import io.ktor.utils.io.readUTF8Line
import io.ktor.client.statement.bodyAsChannel
import io.ktor.client.request.preparePost
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.header
import io.ktor.client.request.request
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsBytes
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.contentType
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put

/** Rôle d'un compte, tel que le serveur le rend. */
object ServerRole {
    const val USER = "user"
    const val SUPRA = "supra_user"
}

/** Jetons rendus par la connexion et le renouvellement. */
@Serializable
data class ServerTokens(
    val user_id: String,
    val device_id: String,
    val access_token: String,
    /** Durée de vie du jeton d'accès, en secondes. */
    val expires_in: Long,
    val refresh_token: String,
    val role: String = ServerRole.USER,
)

@Serializable
data class ServerMe(val user_id: String, val device_id: String, val email: String? = null, val role: String = ServerRole.USER)

/** Un compte, vu du back-office. */
@Serializable
data class ServerAccount(
    val id: String,
    val email: String,
    val role: String,
    val created_at_ms: Long,
    val devices: Long,
    /** Dernier passage d'un de ses appareils ; null s'il ne s'est jamais synchronisé. */
    val last_seen_ms: Long? = null,
    val storage_used_bytes: Long,
    val storage_limit_bytes: Long,
)

/** Appareils d'une plateforme. */
@Serializable
data class ServerPlatformCount(val platform: String, val devices: Long)

/** Une page du site et ses vues. */
@Serializable
data class ServerPageViews(val path: String, val views: Long)

/** Les visites du site, comptées par le serveur. */
@Serializable
data class ServerSiteStats(
    val views_today: Long = 0,
    val views_7d: Long = 0,
    val views_30d: Long = 0,
    val visitors_today: Long = 0,
    val visitors_7d: Long = 0,
    val visitors_30d: Long = 0,
    val top_pages_30d: List<ServerPageViews> = emptyList(),
    /** D'où viennent les visiteurs : le domaine de la page d'avant, `direct` sans lui. */
    val referrers_30d: List<ServerPageViews> = emptyList(),
    /** L'application dans le navigateur : ouvertures et visiteurs distincts sur 30 jours. */
    val app_views_30d: Long = 0,
    val app_visitors_30d: Long = 0,
    /** Téléchargements comptés sur 30 jours, par plateforme (`path` = la plateforme). */
    val downloads_30d: List<ServerPageViews> = emptyList(),
)

/** Les comptes créés une semaine donnée (lundi, AAAA-MM-JJ). */
@Serializable
data class ServerWeekCount(val week: String, val accounts: Long)

/** L'activité des comptes, vue du back-office. */
@Serializable
data class ServerStats(
    val site: ServerSiteStats? = null,
    val accounts: Long,
    val active_today: Long,
    val active_7d: Long,
    val active_30d: Long,
    val online_now: Long,
    val devices: List<ServerPlatformCount> = emptyList(),
    /** Les comptes créés par semaine sur les huit dernières, la plus ancienne d'abord. */
    val signups_by_week: List<ServerWeekCount> = emptyList(),
)

/** Une publication du site, vue du back-office. */
@Serializable
data class ServerPublication(
    val id: String,
    val source_kind: String,
    val source_id: String,
    val slug: String,
    val title: String,
    val status: String,
    val published_at_ms: Long,
    /** `fr` ou `en`. */
    val lang: String = "fr",
    /** Commune aux versions d'un même contenu dans les deux langues. */
    val translation_key: String = "",
    /** Le contenu figé : rendu seulement à la lecture d'une publication, pas dans la liste. */
    val content: JsonObject? = null,
    /** Place d'une page sur le site : `home`, `about`, ou rien (page libre, ou pas une page). */
    val role: String? = null,
    /** Semé par « Remplir avec des exemples », pas encore remplacé. */
    val example: Boolean = false,
    /** La série d'un article du blog, s'il en est un épisode. */
    val series: ServerSeries? = null,
    /** Sur l'accueil du site, et à quel rang. */
    val featured: Boolean = false,
    val featured_rank: Long? = null,
) {
    val isPage: Boolean get() = source_kind == "page"
    val online: Boolean get() = status == "published"
}

/** Une publication lue depuis son JSON, telle que le serveur la rend : pour les bancs d'essai. */
fun parseServerPublication(text: String): ServerPublication =
    Json { ignoreUnknownKeys = true }.decodeFromString(ServerPublication.serializer(), text)

/** Une série d'articles : la clé est commune aux deux langues, le titre est celui de la langue. */
@Serializable
data class ServerSeries(val key: String, val title: String, val episode: Int)

/** Ce que l'accueil dit en plus : présentation de l'appli et bandeau de contact. Un champ vide laisse le site à son texte de repli. */
@Serializable
data class HomeExtras(
    val app_lead: String = "",
    val app_points: List<String> = emptyList(),
    val cta_title: String = "",
    val cta_body: String = "",
    val cta_button: String = "",
)

/** Un lien de la page À propos : profil, dépôt, CV. */
@Serializable
data class AboutLink(val label: String = "", val url: String = "")

/** Comment joindre l'auteur, dit par la page À propos. Un champ vide n'est pas montré. */
@Serializable
data class AboutExtras(val email: String = "", val phone: String = "", val links: List<AboutLink> = emptyList())

/** Présentation d'un article du blog : un champ vide est retiré. */
data class ArticleMeta(val subtitle: String, val summary: String, val cover: String, val series: ServerSeries?)

/** Ce que tout contenu rédigé dans le back-office porte en plus : couverture et référencement. */
data class Presentation(val cover: String = "", val coverAlt: String = "", val seoTitle: String = "", val seoDescription: String = "")

/** Un article ou une pièce écrits dans le back-office, tels qu'on les envoie. */
data class Draft(
    val id: String?,
    /** `note` ou `portfolio_piece`. */
    val kind: String,
    val slug: String,
    val lang: String,
    val title: String,
    val body: String,
    val published: Boolean,
    val translationOf: String? = null,
    val subtitle: String = "",
    val summary: String = "",
    val tags: List<String> = emptyList(),
    val series: ServerSeries? = null,
    val dateStart: String = "",
    val dateEnd: String = "",
    val links: List<Pair<String, String>> = emptyList(),
    val images: List<Pair<String, String>> = emptyList(),
    val presentation: Presentation = Presentation(),
)

/** Ce que rend une écriture de publication : son identifiant et son adresse. */
@Serializable
data class ServerPublished(val id: String, val slug: String = "", val version: Long = 0)

@Serializable
data class ServerImageId(val id: String)

/** Ce qu'ont fait « Remplir avec des exemples » ou « Retirer les exemples ». */
@Serializable
data class ServerExamples(val count: Int = 0, val skipped: Int = 0)

/** Les réglages du site : adresses de téléchargement et portraits. */
@Serializable
data class ServerSite(val downloads: Map<String, String> = emptyMap(), val photos: List<ServerSitePhoto> = emptyList())

@Serializable
data class ServerSignedUrl(val url: String, val method: String = "PUT", val expires_in: Long = 0)

/** Le modèle de langue de l'assistant, tel que le serveur le rend : jamais la clé, seulement [has_key]. */
@Serializable
data class ServerAssistantModel(
    val url: String,
    val name: String,
    val kind: String = "openai",
    val window: Int = 8192,
    val has_key: Boolean = false,
    val updated_at_ms: Long = 0,
)

/** Le résultat du bouton « Tester » : la latence et la réponse, ou un code. */
@Serializable
data class ServerModelTest(val ok: Boolean, val latency_ms: Long? = null, val answer: String? = null, val code: String? = null)

@Serializable
data class ServerSitePhoto(val slot: Int, val content_type: String = "", val size_bytes: Long = 0, val updated_at_ms: Long = 0)

/** Un fichier envoyé au serveur, vu de la rubrique Médias : avec le contenu qui le porte. */
@Serializable
data class ServerSiteFile(
    val id: String,
    val file_name: String,
    val mime_type: String = "",
    val size_bytes: Long = 0,
    val created_at_ms: Long = 0,
    val owner_kind: String = "",
    val owner_id: String = "",
    /** Titre de la publication qui porte ce fichier, s'il y en a une. */
    val owner_title: String? = null,
    /** Le contenu qui le porte est en ligne : le site sert ce fichier. */
    val online: Boolean = false,
)

/**
 * Refus du serveur : son statut HTTP et son code (« forbidden », « invalid_credentials »…).
 * [status] vaut 0 quand le serveur n'a pas pu être joint.
 */
/**
 * Découpe un flux SSE ligne à ligne : rend (nom, données) à la ligne vide qui termine
 * un événement. Les lignes de commentaire (« : ») sont ignorées.
 */
class SseLineParser {
    private var event: String? = null
    private val data = StringBuilder()
    private var hasData = false

    fun line(raw: String): Pair<String, String>? {
        val line = raw.removeSuffix("\r")
        if (line.isEmpty()) {
            if (!hasData) { event = null; return null }
            val out = (event ?: "message") to data.toString()
            event = null; data.clear(); hasData = false
            return out
        }
        if (line.startsWith(":")) return null
        val field = line.substringBefore(':')
        val value = line.substringAfter(':', "").removePrefix(" ")
        when (field) {
            "event" -> event = value
            "data" -> { if (hasData) data.append('\n'); data.append(value); hasData = true }
        }
        return null
    }
}

/** Le compte de ressources d'une compétence, vu par le serveur. */
@Serializable
data class ServerResourcesUsage(val skill_id: String, val resource_count: Int, val resource_limit: Int)

class ServerException(val status: Int, val code: String, message: String, cause: Throwable? = null) : Exception(message, cause)

/**
 * Les appels au serveur teddApp, sans état : chaque appel reçoit le jeton dont il a besoin.
 * La tenue de la session (jetons, renouvellement) est l'affaire de [ServerSession].
 */
class ServerApi(private val http: HttpClient, baseUrl: String) {
    private val base = baseUrl.trim().trimEnd('/')
    private val json = Json { ignoreUnknownKeys = true }

    suspend fun login(email: String, password: String, deviceId: String, deviceName: String, platform: String): ServerTokens =
        call(HttpMethod.Post, "/v1/auth/login", ServerTokens.serializer()) {
            jsonBody(buildJsonObject {
                put("email", email); put("password", password); put("device_id", deviceId)
                put("device_name", deviceName); put("platform", platform)
            })
        }

    /** Crée un compte sur le serveur, si les inscriptions sont ouvertes, et ouvre sa session. */
    suspend fun signup(email: String, password: String, deviceId: String, deviceName: String, platform: String): ServerTokens =
        call(HttpMethod.Post, "/v1/auth/signup", ServerTokens.serializer()) {
            jsonBody(buildJsonObject {
                put("email", email); put("password", password); put("device_id", deviceId)
                put("device_name", deviceName); put("platform", platform)
            })
        }

    suspend fun refresh(refreshToken: String): ServerTokens =
        call(HttpMethod.Post, "/v1/auth/refresh", ServerTokens.serializer()) {
            jsonBody(buildJsonObject { put("refresh_token", refreshToken) })
        }

    suspend fun logout(access: String) = call(HttpMethod.Post, "/v1/auth/logout", null, access)

    suspend fun me(access: String): ServerMe = call(HttpMethod.Get, "/v1/me", ServerMe.serializer(), access)

    // ── Back-office ────────────────────────────────────────────────────

    suspend fun stats(access: String): ServerStats = call(HttpMethod.Get, "/v1/admin/stats", ServerStats.serializer(), access)

    suspend fun accounts(access: String): List<ServerAccount> =
        call(HttpMethod.Get, "/v1/admin/users", ListSerializer(ServerAccount.serializer()), access)

    suspend fun createAccount(access: String, email: String, password: String) =
        call(HttpMethod.Post, "/v1/admin/users", null, access) {
            jsonBody(buildJsonObject { put("email", email); put("password", password) })
        }

    suspend fun revokeAccount(access: String, id: String) = call(HttpMethod.Post, "/v1/admin/users/$id/revoke", null, access)

    suspend fun setQuota(access: String, id: String, bytes: Long) =
        call(HttpMethod.Put, "/v1/admin/users/$id/quota", null, access) {
            jsonBody(buildJsonObject { put("storage_limit_bytes", bytes) })
        }

    suspend fun deleteAccount(access: String, id: String) = call(HttpMethod.Delete, "/v1/admin/users/$id", null, access)

    suspend fun publications(access: String): List<ServerPublication> =
        call(HttpMethod.Get, "/v1/publications", ListSerializer(ServerPublication.serializer()), access)

    suspend fun publication(access: String, id: String): ServerPublication =
        call(HttpMethod.Get, "/v1/publications/$id", ServerPublication.serializer(), access)

    /** Publie un élément déjà synchronisé, ou republie sa version courante. */
    /** [article] absent : le serveur garde la présentation d'avant. */
    suspend fun publish(
        access: String, kind: String, sourceId: String, slug: String, lang: String, translationOf: String?,
        article: ArticleMeta? = null,
    ) =
        call(HttpMethod.Post, "/v1/publications", null, access) {
            jsonBody(buildJsonObject {
                put("source_kind", kind); put("source_id", sourceId); put("slug", slug); put("lang", lang)
                if (translationOf != null) put("translation_of", translationOf)
                if (article != null) put("article", buildJsonObject {
                    put("subtitle", article.subtitle); put("summary", article.summary); put("cover", article.cover)
                    article.series?.let { s -> put("series", buildJsonObject { put("key", s.key); put("title", s.title); put("episode", s.episode) }) }
                })
            })
        }

    /** Crée ou réécrit une page du site. [id] absent : nouvelle page. */
    suspend fun savePage(
        access: String, id: String?, slug: String, lang: String, title: String, body: String,
        role: String?, translationOf: String?, published: Boolean,
        stats: List<Pair<String, String>> = emptyList(),
        home: HomeExtras? = null,
        about: AboutExtras? = null,
        presentation: Presentation? = null,
    ): ServerPublished = call(HttpMethod.Post, "/v1/pages", ServerPublished.serializer(), access) {
        jsonBody(buildJsonObject {
            if (id != null) put("id", id)
            put("slug", slug); put("lang", lang); put("title", title); put("body", body)
            if (role != null) put("role", role)
            if (translationOf != null) put("translation_of", translationOf)
            put("published", published)
            if (stats.isNotEmpty()) put("stats", JsonArray(stats.map { (value, label) -> buildJsonObject { put("value", value); put("label", label) } }))
            if (home != null) put("home", json.encodeToJsonElement(HomeExtras.serializer(), home))
            if (about != null) put("about", json.encodeToJsonElement(AboutExtras.serializer(), about))
            presentation?.let { p -> put("cover", p.cover); put("cover_alt", p.coverAlt); put("seo_title", p.seoTitle); put("seo_description", p.seoDescription) }
        })
    }

    /**
     * Enregistre un article ou une pièce écrits dans le back-office, en brouillon ou en
     * ligne. [id] absent : nouveau contenu ; présent : réécrit, détaché de sa source.
     */
    suspend fun saveDraft(access: String, draft: Draft): ServerPublished =
        call(HttpMethod.Post, "/v1/drafts", ServerPublished.serializer(), access) {
            jsonBody(buildJsonObject {
                draft.id?.let { put("id", it) }
                put("source_kind", draft.kind); put("slug", draft.slug); put("lang", draft.lang)
                put("title", draft.title); put("body", draft.body); put("published", draft.published)
                draft.translationOf?.let { put("translation_of", it) }
                if (draft.kind == "note") {
                    put("tags", JsonArray(draft.tags.map { JsonPrimitive(it) }))
                    put("article", buildJsonObject {
                        put("subtitle", draft.subtitle); put("summary", draft.summary)
                        put("cover", draft.presentation.cover); put("cover_alt", draft.presentation.coverAlt)
                        put("seo_title", draft.presentation.seoTitle); put("seo_description", draft.presentation.seoDescription)
                        draft.series?.let { s -> put("series", buildJsonObject { put("key", s.key); put("title", s.title); put("episode", s.episode) }) }
                    })
                } else {
                    put("subtitle", draft.subtitle); put("date_start", draft.dateStart); put("date_end", draft.dateEnd)
                    put("links", JsonArray(draft.links.map { (label, url) -> buildJsonObject { put("label", label); put("url", url) } }))
                    put("images", JsonArray(draft.images.map { (url, caption) -> buildJsonObject { put("url", url); put("caption", caption) } }))
                    put("cover", draft.presentation.cover); put("cover_alt", draft.presentation.coverAlt)
                    put("seo_title", draft.presentation.seoTitle); put("seo_description", draft.presentation.seoDescription)
                }
            })
        }

    /** Met un contenu sur l'accueil du site, à un rang, ou l'en retire. */
    suspend fun feature(access: String, id: String, featured: Boolean, rank: Int? = null): ServerPublished =
        call(HttpMethod.Post, "/v1/publications/$id/feature", ServerPublished.serializer(), access) {
            jsonBody(buildJsonObject { put("featured", featured); if (rank != null) put("rank", rank) })
        }

    /** Met en ligne une publication telle qu'elle est : brouillon, exemple ou contenu retiré. */
    suspend fun setOnline(access: String, id: String): ServerPublished =
        call(HttpMethod.Post, "/v1/publications/$id/publish", ServerPublished.serializer(), access)

    /** Efface une publication, pour de bon. */
    suspend fun deleteDraft(access: String, id: String) = call(HttpMethod.Delete, "/v1/drafts/$id", null, access)

    /** Dépose une image depuis l'éditeur ; rend son identifiant, à citer `file:<id>`. */
    suspend fun uploadSiteImage(access: String, bytes: ByteArray, contentType: String, fileName: String): String =
        call(HttpMethod.Post, "/v1/site/images", ServerImageId.serializer(), access) {
            contentType(ContentType.parse(contentType))
            header("X-File-Name", fileName)
            setBody(bytes)
        }.id

    /** Les octets d'une image de l'éditeur, pour la montrer avant qu'elle soit en ligne. */
    suspend fun siteImage(access: String, id: String): ByteArray {
        val response = http.request(base + "/v1/site/images/$id") {
            method = HttpMethod.Get
            header(HttpHeaders.Authorization, "Bearer $access")
        }
        if (response.status.value !in 200..299) throw ServerException(response.status.value, "http_${response.status.value}", "Image indisponible.")
        return response.bodyAsBytes()
    }

    suspend fun deleteSiteImage(access: String, id: String) = call(HttpMethod.Delete, "/v1/site/images/$id", null, access)

    /** Les fichiers envoyés par le compte, les plus récents d'abord. */
    suspend fun siteFiles(access: String): List<ServerSiteFile> =
        call(HttpMethod.Get, "/v1/site/files", ListSerializer(ServerSiteFile.serializer()), access)

    /** Sème en ligne les exemples qui manquent, dans les deux langues. */
    suspend fun fillExamples(access: String): ServerExamples =
        call(HttpMethod.Post, "/v1/site/examples", ServerExamples.serializer(), access)

    /** Efface les exemples qui n'ont pas été réécrits. */
    suspend fun removeExamples(access: String): ServerExamples =
        call(HttpMethod.Delete, "/v1/site/examples", ServerExamples.serializer(), access)

    suspend fun site(access: String): ServerSite = call(HttpMethod.Get, "/v1/site", ServerSite.serializer(), access)

    suspend fun saveSite(access: String, downloads: Map<String, String>): ServerSite =
        call(HttpMethod.Put, "/v1/site", ServerSite.serializer(), access) {
            jsonBody(buildJsonObject { put("downloads", buildJsonObject { downloads.forEach { (platform, url) -> put(platform, url) } }) })
        }

    /** Dépose une photo du site : [contentType] est `image/jpeg`, `image/png` ou `image/webp`. */
    suspend fun uploadSitePhoto(access: String, slot: Int, bytes: ByteArray, contentType: String) =
        call(HttpMethod.Put, "/v1/site/photos/$slot", null, access) {
            contentType(ContentType.parse(contentType))
            setBody(bytes)
        }

    // ── Modèle de l'assistant ──────────────────────────────────────────
    // Un réglage du serveur, hors synchronisation : la clé n'en revient jamais.

    /** Le réglage en place, ou `null` s'il n'y en a pas. */
    suspend fun assistantModel(access: String): ServerAssistantModel? {
        val text = send(HttpMethod.Get, "/v1/assistant/model", access) {}
        if (text.isBlank()) return null
        return try {
            json.decodeFromString(ServerAssistantModel.serializer(), text)
        } catch (e: Exception) {
            throw ServerException(200, "bad_response", "Réponse du serveur illisible.")
        }
    }

    /** Enregistre le réglage. [apiKey] `null` garde la clé en place, vide l'efface. */
    suspend fun saveAssistantModel(access: String, url: String, name: String, kind: String, window: Int, apiKey: String?): ServerAssistantModel =
        call(HttpMethod.Put, "/v1/assistant/model", ServerAssistantModel.serializer(), access) {
            jsonBody(buildJsonObject {
                put("url", url); put("name", name); put("kind", kind); put("window", window)
                if (apiKey != null) put("api_key", apiKey)
            })
        }

    suspend fun deleteAssistantModel(access: String) = call(HttpMethod.Delete, "/v1/assistant/model", null, access)

    /** Une question d'un mot au modèle, par le RAG. */
    suspend fun testAssistantModel(access: String): ServerModelTest =
        call(HttpMethod.Post, "/v1/assistant/model/test", ServerModelTest.serializer(), access)

    // ── Fichiers joints ────────────────────────────────────────────────
    // Le serveur ne reçoit pas les octets : il signe une adresse du stockage de fichiers,
    // où l'appareil dépose ou lit directement.

    suspend fun fileUploadUrl(access: String, id: String) =
        call(HttpMethod.Post, "/v1/files/$id/upload-url", ServerSignedUrl.serializer(), access)

    suspend fun fileComplete(access: String, id: String) = call(HttpMethod.Post, "/v1/files/$id/complete", null, access)

    /**
     * Pose une question à l'assistant d'une compétence, dans une conversation déjà connue
     * du serveur. La réponse arrive en flux : [onEvent] reçoit chaque événement (nom, données
     * JSON) au fil de l'eau. Un refus avant le flux lève une [ServerException] ; un flux
     * coupé, une [ServerException] de code `stream_cut`. Annuler la coroutine ferme la
     * connexion : le serveur garde alors la réponse « interrompue ».
     */
    suspend fun ask(
        access: String,
        skillId: String,
        conversationId: String,
        question: String,
        onEvent: suspend (event: String, data: String) -> Unit,
    ) {
        val statement = http.preparePost("$base/v1/skills/$skillId/conversations/$conversationId/ask") {
            header(HttpHeaders.Authorization, "Bearer $access")
            header(HttpHeaders.Accept, "text/event-stream")
            jsonBody(buildJsonObject { put("question", question) })
            // La réponse peut durer deux minutes : le délai par défaut du client la couperait.
            if (http.pluginOrNull(io.ktor.client.plugins.HttpTimeout) != null) {
                timeout { requestTimeoutMillis = 180_000; socketTimeoutMillis = 60_000 }
            }
        }
        var streaming = false
        try {
            statement.execute { response ->
                if (response.status.value !in 200..299) {
                    val text = response.bodyAsText()
                    val error = runCatching { json.parseToJsonElement(text).jsonObject["error"]?.jsonObject }.getOrNull()
                    throw ServerException(
                        status = response.status.value,
                        code = error?.get("code")?.jsonPrimitive?.content ?: "http_${response.status.value}",
                        message = error?.get("message")?.jsonPrimitive?.content ?: "Le serveur a répondu ${response.status.value}.",
                    )
                }
                streaming = true
                val channel = response.bodyAsChannel()
                val parser = SseLineParser()
                while (true) {
                    val line = channel.readUTF8Line() ?: break
                    parser.line(line)?.let { (event, data) -> onEvent(event, data) }
                }
                parser.line("")?.let { (event, data) -> onEvent(event, data) }
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (e: ServerException) {
            throw e
        } catch (e: Exception) {
            if (streaming) throw ServerException(0, "stream_cut", "La connexion s'est coupée pendant la réponse.", e)
            val cause = (e.message ?: e::class.simpleName ?: "").take(120)
            throw ServerException(0, "unreachable", "Serveur injoignable. Vérifie ta connexion. ($cause)", e)
        }
    }

    /** Relance l'analyse d'une ressource : actualiser un lien, ou réessayer après un échec. */
    suspend fun reindexResource(access: String, id: String) =
        call(HttpMethod.Post, "/v1/skill-resources/$id/reindex", null, access)

    /** Le nombre de ressources d'une compétence qui fait foi pour le quota. */
    suspend fun resourcesUsage(access: String, skillId: String): ServerResourcesUsage =
        call(HttpMethod.Get, "/v1/skills/$skillId/resources/usage", ServerResourcesUsage.serializer(), access)

    suspend fun fileDownloadUrl(access: String, id: String) =
        call(HttpMethod.Get, "/v1/files/$id/download-url", ServerSignedUrl.serializer(), access)

    /** Dépose [bytes] à une adresse signée du stockage. */
    suspend fun putSigned(url: String, bytes: ByteArray, contentType: String) {
        val response = http.request(url) {
            method = HttpMethod.Put
            contentType(ContentType.parse(contentType))
            setBody(bytes)
        }
        if (response.status.value !in 200..299) {
            throw ServerException(response.status.value, "upload_failed", "Le stockage a refusé le fichier (${response.status.value}).")
        }
    }

    /** Lit les octets à une adresse signée du stockage. */
    suspend fun getSigned(url: String): ByteArray {
        val response = http.request(url) { method = HttpMethod.Get }
        if (response.status.value !in 200..299) {
            throw ServerException(response.status.value, "download_failed", "Le stockage n'a pas rendu le fichier (${response.status.value}).")
        }
        return response.bodyAsBytes()
    }

    suspend fun deleteSitePhoto(access: String, slot: Int) = call(HttpMethod.Delete, "/v1/site/photos/$slot", null, access)

    suspend fun unpublish(access: String, id: String) = call(HttpMethod.Delete, "/v1/publications/$id", null, access)

    // ── Synchronisation ────────────────────────────────────────────────

    /** Envoie un lot de mutations. Rend la réponse du serveur : un accusé par mutation. */
    suspend fun push(access: String, mutations: kotlinx.serialization.json.JsonArray): JsonObject =
        json.parseToJsonElement(
            send(HttpMethod.Post, "/v1/sync/push", access) { jsonBody(buildJsonObject { put("mutations", mutations) }) },
        ).jsonObject

    /** Une page de changements après le curseur [after]. */
    suspend fun pull(access: String, after: Long, limit: Int): JsonObject =
        json.parseToJsonElement(send(HttpMethod.Get, "/v1/sync/pull?after=$after&limit=$limit", access) {}).jsonObject

    // ── Journal du noyau ───────────────────────────────────────────────

    /**
     * Publie des faits du noyau, déjà écrits en JSON par lui. Rend les enregistrements
     * globaux `{"global_offset":n,"envelope":{…}}`, en JSON eux aussi.
     */
    suspend fun publishEvents(access: String, envelopes: List<String>): List<String> {
        val body = envelopes.joinToString(",", prefix = "{\"events\":[", postfix = "]}")
        return records(
            send(HttpMethod.Post, "/v1/events", access) {
                contentType(ContentType.Application.Json)
                setBody(body)
            },
        )
    }

    /** Une page des faits d'offset strictement supérieur à [after], par ordre croissant. */
    suspend fun fetchEvents(access: String, after: Long, limit: Int): List<String> =
        records(send(HttpMethod.Get, "/v1/events?after=$after&limit=$limit", access) {})

    /** L'instantané du noyau rejoué par le serveur sur tout le journal, en JSON. */
    suspend fun coreSnapshot(access: String): String = send(HttpMethod.Get, "/v1/core/snapshot", access) {}

    private fun records(text: String): List<String> =
        runCatching { json.parseToJsonElement(text).jsonObject["records"]!!.jsonArray.map { it.toString() } }
            .getOrElse { throw ServerException(200, "bad_response", "Réponse du serveur illisible.") }

    // ── Transport ──────────────────────────────────────────────────────

    // Pas « body » : HttpRequestBuilder a déjà une propriété de ce nom, qui l'emporterait.
    private fun HttpRequestBuilder.jsonBody(value: JsonObject) {
        contentType(ContentType.Application.Json)
        setBody(value.toString())
    }

    private suspend fun call(method: HttpMethod, path: String, ignored: Nothing?, access: String? = null, configure: HttpRequestBuilder.() -> Unit = {}) {
        send(method, path, access, configure)
    }

    private suspend fun <T> call(
        method: HttpMethod,
        path: String,
        serializer: KSerializer<T>,
        access: String? = null,
        configure: HttpRequestBuilder.() -> Unit = {},
    ): T {
        val text = send(method, path, access, configure)
        return try {
            json.decodeFromString(serializer, text)
        } catch (e: Exception) {
            throw ServerException(200, "bad_response", "Réponse du serveur illisible.")
        }
    }

    private suspend fun send(method: HttpMethod, path: String, access: String?, configure: HttpRequestBuilder.() -> Unit): String {
        val response = try {
            http.request(base + path) {
                this.method = method
                if (access != null) header(HttpHeaders.Authorization, "Bearer $access")
                // Pas « build » : ce serait la méthode de HttpRequestBuilder, pas notre réglage.
                configure()
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (e: Exception) {
            // La cause en clair : un délai dépassé, un réseau coupé ou un certificat refusé ne se soignent pas pareil.
            val cause = (e.message ?: e::class.simpleName ?: "").take(120)
            throw ServerException(0, "unreachable", "Serveur injoignable. Vérifie l'adresse et ta connexion. ($cause)", e)
        }
        val text = response.bodyAsText()
        if (response.status.value in 200..299) return text
        val error = runCatching { json.parseToJsonElement(text).jsonObject["error"]?.jsonObject }.getOrNull()
        throw ServerException(
            status = response.status.value,
            code = error?.get("code")?.jsonPrimitive?.content ?: "http_${response.status.value}",
            message = error?.get("message")?.jsonPrimitive?.content ?: "Le serveur a répondu ${response.status.value}.",
        )
    }
}
